'use client';

import { createClient } from '@/lib/supabase/client';

export type Product = {
  id: string;
  productId: string;
  category: string;
  name: string;
  nameZh: string;
  brand: string;
  specs: string;
  image: string;
  alt: string;
  badge: string;
  badgeColor: string;
  moq: string;
  priceRange: [number, number];
  stockStatus: 'in-stock' | 'low-stock' | 'out-of-stock';
};

function isSchemaError(error: any): boolean {
  if (!error) return false;
  if (error.code && typeof error.code === 'string') {
    const errorClass = error.code.substring(0, 2);
    if (errorClass === '42') return true;
    if (errorClass === '23') return false;
    if (errorClass === '08') return true;
  }
  if (error.message) {
    const schemaErrorPatterns = [
      /relation.*does not exist/i,
      /column.*does not exist/i,
      /function.*does not exist/i,
      /syntax error/i,
      /type.*does not exist/i,
    ];
    return schemaErrorPatterns.some((p) => p.test(error.message));
  }
  return false;
}

function mapRow(row: any): Product {
  return {
    id: row.product_id,
    productId: row.product_id,
    category: row.category,
    name: row.name,
    nameZh: row.name_zh,
    brand: row.brand,
    specs: row.specs,
    image: row.image,
    alt: row.alt,
    badge: row.badge,
    badgeColor: row.badge_color,
    moq: row.moq,
    priceRange: [row.price_min, row.price_max],
    stockStatus: row.stock_status as 'in-stock' | 'low-stock' | 'out-of-stock',
  };
}

export const productService = {
  async getAll(): Promise<Product[]> {
    const supabase = createClient();
    try {
      const { data, error } = await supabase
        .from('products')
        .select('*')
        .eq('is_active', true)
        .order('category', { ascending: true })
        .order('name', { ascending: true });

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error fetching products:', error.message);
          throw error;
        }
        console.log('Data error fetching products:', error.message);
        return [];
      }
      return data?.map(mapRow) || [];
    } catch (error: any) {
      console.log('Product fetch error:', error.message);
      throw error;
    }
  },

  async getById(productId: string): Promise<Product | null> {
    const supabase = createClient();
    try {
      const { data, error } = await supabase
        .from('products')
        .select('*')
        .eq('product_id', productId)
        .eq('is_active', true)
        .single();

      if (error) {
        console.log('Error fetching product by id:', error.message);
        return null;
      }
      return data ? mapRow(data) : null;
    } catch (error: any) {
      console.log('Product fetch by id error:', error.message);
      return null;
    }
  },

  async getRelated(category: string, excludeProductId: string, limit = 4): Promise<Product[]> {
    const supabase = createClient();
    try {
      const { data, error } = await supabase
        .from('products')
        .select('*')
        .eq('category', category)
        .eq('is_active', true)
        .neq('product_id', excludeProductId)
        .limit(limit);

      if (error) {
        console.log('Error fetching related products:', error.message);
        return [];
      }
      return data?.map(mapRow) || [];
    } catch (error: any) {
      console.log('Related products fetch error:', error.message);
      return [];
    }
  },
};
